import React, { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getSubjects, getChapters, getTopics, createSubject, createChapter, createTopic,
         getQuestions, createQuestion, updateQuestion, deleteQuestion } from '../../api';
import MathHtml from '../../components/shared/MathHtml';

// ── helpers ────────────────────────────────────────────────────────────────
const stripHtml = (html) => {
  if (!html) return '';
  const tmp = document.createElement('div');
  tmp.innerHTML = html;
  return tmp.textContent || tmp.innerText || '';
};

const DIFF_BADGE = { easy: 'badge-green', medium: 'badge-blue', hard: 'badge-red' };
const QTYPE_LABEL = {
  mcq: 'MCQ', assertion_reason: 'A/R', match_the_following: 'Match',
  image_mcq: 'Image', passage_based: 'Passage', how_many_correct: 'HMC',
};

const emptyQ = {
  topic_id: '', question_text: '', option_a: '', option_b: '',
  option_c: '', option_d: '', correct_option: 'A', difficulty: 'medium',
  question_type: 'mcq', explanation: '', marks_correct: 4, marks_wrong: -1,
  year: '', source: '',
};

// ── inline subject/chapter/topic creator ──────────────────────────────────
function InlineCreate({ label, onSave }) {
  const [val, setVal] = useState('');
  const [show, setShow] = useState(false);
  if (!show) return (
    <button className="btn btn-ghost btn-sm" style={{ marginTop: 4 }} onClick={() => setShow(true)}>
      + New {label}
    </button>
  );
  return (
    <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
      <input className="form-input" style={{ flex: 1 }} placeholder={`${label} name`} value={val}
        onChange={e => setVal(e.target.value)} autoFocus />
      <button className="btn btn-primary btn-sm"
        onClick={() => { if (val.trim()) { onSave(val.trim()); setVal(''); setShow(false); } }}>Add</button>
      <button className="btn btn-ghost btn-sm" onClick={() => setShow(false)}>✕</button>
    </div>
  );
}

// ── view modal ────────────────────────────────────────────────────────────
function ViewModal({ question, onClose }) {
  if (!question) return null;
  const opts = ['A', 'B', 'C', 'D'];
  const optionHtml = (letter) => question[`option_${letter.toLowerCase()}`] || '';
  const imgUrl = (letter) => question[`option_${letter.toLowerCase()}_image_url`];

  return (
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 1000,
      display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
      padding: '40px 16px', overflowY: 'auto',
    }}>
      <div onClick={e => e.stopPropagation()} style={{
        background: 'var(--surface)', borderRadius: 12, width: '100%', maxWidth: 760,
        padding: 32, boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
      }}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {question.subject_name && (
              <span className="badge badge-blue">{question.subject_name}</span>
            )}
            {question.chapter_name && (
              <span className="badge" style={{ background: 'var(--primary-light, #e8f4fd)', color: 'var(--primary)' }}>
                {question.chapter_name}
              </span>
            )}
            {question.topic_name && (
              <span className="badge" style={{ background: 'var(--surface-alt, #f5f5f5)', color: 'var(--text-muted)' }}>
                {question.topic_name}
              </span>
            )}
            <span className="badge" style={{ background: 'var(--border)' }}>
              {QTYPE_LABEL[question.question_type] || question.question_type}
            </span>
            {question.difficulty && (
              <span className={`badge ${DIFF_BADGE[question.difficulty] || 'badge-blue'}`}>
                {question.difficulty}
              </span>
            )}
            {question.year && <span className="badge">{question.year}</span>}
            {question.source && (
              <span className="badge" style={{ background: 'var(--border)' }}>{question.source}</span>
            )}
          </div>
          <button className="btn btn-ghost btn-sm" onClick={onClose} style={{ flexShrink: 0, marginLeft: 12 }}>✕ Close</button>
        </div>

        {/* Passage */}
        {question.passage_text && (
          <MathHtml tag="div" style={{ background: 'var(--surface-alt, #f9f9f9)', borderRadius: 8, padding: '12px 16px', marginBottom: 16,
            borderLeft: '3px solid var(--primary)', fontSize: 14, lineHeight: 1.7 }}
            html={question.passage_text} />
        )}

        {/* Question */}
        {question.question_image_url && (
          <img src={question.question_image_url} alt="Question"
            style={{ maxWidth: '100%', borderRadius: 8, marginBottom: 12 }} />
        )}
        <MathHtml tag="div" style={{ fontSize: 16, lineHeight: 1.7, marginBottom: 20 }}
          html={question.question_text} />

        {/* Options */}
        <div style={{ display: 'grid', gap: 10, marginBottom: 20 }}>
          {opts.map(letter => {
            const isCorrect = question.correct_option === letter;
            return (
              <div key={letter} style={{
                display: 'flex', alignItems: 'flex-start', gap: 12, padding: '10px 14px',
                borderRadius: 8, border: `2px solid ${isCorrect ? 'var(--success)' : 'var(--border)'}`,
                background: isCorrect ? 'rgba(34,197,94,0.06)' : 'transparent',
              }}>
                <span style={{
                  width: 28, height: 28, borderRadius: 7, flexShrink: 0,
                  background: isCorrect ? 'var(--success)' : 'var(--border)',
                  color: isCorrect ? 'white' : 'var(--text-muted)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontWeight: 700, fontSize: 13,
                }}>{letter}</span>
                <div style={{ flex: 1, fontSize: 14, lineHeight: 1.6, paddingTop: 3 }}>
                  <MathHtml tag="span" html={optionHtml(letter)} />
                  {imgUrl(letter) && (
                    <img src={imgUrl(letter)} alt={`Option ${letter}`}
                      style={{ display: 'block', maxWidth: '100%', marginTop: 8, borderRadius: 6 }} />
                  )}
                </div>
                {isCorrect && (
                  <span style={{ fontSize: 12, color: 'var(--success)', fontWeight: 600, paddingTop: 5 }}>✓ Correct</span>
                )}
              </div>
            );
          })}
        </div>

        {/* Explanation */}
        {(question.explanation || question.explanation_image_url) && (
          <div style={{ borderTop: '1px solid var(--border)', paddingTop: 16 }}>
            <p style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 8,
              textTransform: 'uppercase', letterSpacing: '0.05em' }}>Explanation</p>
            {question.explanation_image_url && (
              <img src={question.explanation_image_url} alt="Explanation"
                style={{ maxWidth: '100%', borderRadius: 8, marginBottom: 10 }} />
            )}
            {question.explanation && (
              <MathHtml tag="div" style={{ fontSize: 14, lineHeight: 1.7, color: 'var(--text-secondary, var(--text-muted))' }}
                html={question.explanation} />
            )}
          </div>
        )}

        {/* Marks */}
        <div style={{ display: 'flex', gap: 16, marginTop: 16, paddingTop: 16, borderTop: '1px solid var(--border)', fontSize: 13 }}>
          {question.marks_correct != null && (
            <span style={{ color: 'var(--success)' }}>+{question.marks_correct} correct</span>
          )}
          {question.marks_wrong != null && (
            <span style={{ color: 'var(--error, #ef4444)' }}>{question.marks_wrong} wrong</span>
          )}
          <span style={{ color: 'var(--text-muted)', marginLeft: 'auto' }}>ID #{question.id}</span>
        </div>
      </div>
    </div>
  );
}

// ── main component ────────────────────────────────────────────────────────
export default function AdminQuestions() {
  const qc = useQueryClient();

  // filter state
  const [filter, setFilter] = useState({ subject_id: '', chapter_id: '', topic_id: '' });

  // sort state: { col: 'subject_name'|'chapter_name'|'id', dir: 'asc'|'desc' }
  const [sort, setSort] = useState({ col: 'id', dir: 'desc' });

  // view modal
  const [viewQ, setViewQ] = useState(null);

  // edit/create form
  const [form, setForm] = useState(emptyQ);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [formSubject, setFormSubject] = useState('');
  const [formChapter, setFormChapter] = useState('');
  const [error, setError] = useState('');

  // ── data fetching ──────────────────────────────────────────────────────
  const subjects = useQuery({ queryKey: ['subjects'], queryFn: () => getSubjects().then(r => r.data) });

  const filterChapters = useQuery({
    queryKey: ['chapters', filter.subject_id],
    queryFn: () => getChapters(filter.subject_id).then(r => r.data),
    enabled: !!filter.subject_id,
  });
  const filterTopics = useQuery({
    queryKey: ['topics', filter.chapter_id],
    queryFn: () => getTopics(filter.chapter_id).then(r => r.data),
    enabled: !!filter.chapter_id,
  });

  const formChapters = useQuery({
    queryKey: ['chapters', formSubject],
    queryFn: () => getChapters(formSubject).then(r => r.data),
    enabled: !!formSubject,
  });
  const formTopics = useQuery({
    queryKey: ['topics', formChapter],
    queryFn: () => getTopics(formChapter).then(r => r.data),
    enabled: !!formChapter,
  });

  const questionsQuery = useQuery({
    queryKey: ['questions', filter],
    queryFn: () => getQuestions({
      subject_id: filter.subject_id || undefined,
      chapter_id: filter.chapter_id || undefined,
      topic_id:   filter.topic_id   || undefined,
    }).then(r => r.data),
  });

  // ── client-side sort ───────────────────────────────────────────────────
  const sortedQuestions = useMemo(() => {
    const data = questionsQuery.data || [];
    return [...data].sort((a, b) => {
      const va = (a[sort.col] ?? '').toString().toLowerCase();
      const vb = (b[sort.col] ?? '').toString().toLowerCase();
      const cmp = sort.col === 'id'
        ? (Number(a.id) - Number(b.id))
        : va.localeCompare(vb);
      return sort.dir === 'asc' ? cmp : -cmp;
    });
  }, [questionsQuery.data, sort]);

  const toggleSort = (col) => {
    setSort(s => s.col === col ? { col, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { col, dir: 'asc' });
  };

  const SortIcon = ({ col }) => {
    if (sort.col !== col) return <span style={{ opacity: 0.3, marginLeft: 4 }}>⇅</span>;
    return <span style={{ marginLeft: 4 }}>{sort.dir === 'asc' ? '↑' : '↓'}</span>;
  };

  // ── mutations ──────────────────────────────────────────────────────────
  const createSubjectMut = useMutation({
    mutationFn: createSubject,
    onSuccess: () => qc.invalidateQueries(['subjects']),
  });
  const createChapterMut = useMutation({
    mutationFn: ({ sid, name }) => createChapter(sid, { name, subject_id: sid }),
    onSuccess: (_, v) => qc.invalidateQueries(['chapters', v.sid]),
  });
  const createTopicMut = useMutation({
    mutationFn: ({ cid, name }) => createTopic(cid, { name, chapter_id: cid }),
    onSuccess: (_, v) => qc.invalidateQueries(['topics', v.cid]),
  });

  const createQ = useMutation({
    mutationFn: createQuestion,
    onSuccess: () => {
      qc.invalidateQueries(['questions']);
      setForm(emptyQ); setShowForm(false); setEditing(null); setError('');
    },
    onError: (e) => setError(e.response?.data?.detail || 'Error saving question'),
  });
  const updateQ = useMutation({
    mutationFn: ({ id, data }) => updateQuestion(id, data),
    onSuccess: () => {
      qc.invalidateQueries(['questions']);
      setForm(emptyQ); setShowForm(false); setEditing(null); setError('');
    },
    onError: (e) => setError(e.response?.data?.detail || 'Error updating question'),
  });
  const deleteQ = useMutation({
    mutationFn: deleteQuestion,
    onSuccess: () => qc.invalidateQueries(['questions']),
  });

  // ── form handlers ──────────────────────────────────────────────────────
  const handleSubmit = (e) => {
    e.preventDefault(); setError('');
    if (!form.topic_id) { setError('Please select a topic'); return; }
    const payload = {
      ...form,
      topic_id:      parseInt(form.topic_id),
      marks_correct: form.marks_correct !== '' ? parseFloat(form.marks_correct) : null,
      marks_wrong:   form.marks_wrong   !== '' ? parseFloat(form.marks_wrong)   : null,
      year:          form.year          !== '' ? parseInt(form.year)            : null,
      explanation:   form.explanation   || null,
      source:        form.source        || null,
    };
    if (editing) updateQ.mutate({ id: editing, data: payload });
    else createQ.mutate(payload);
  };

  const startEdit = (q) => {
    setEditing(q.id);
    setShowForm(true);
    // Populate subject/chapter dropdowns so cascades work
    setFormSubject(q.subject_id ? String(q.subject_id) : '');
    setFormChapter(q.chapter_id ? String(q.chapter_id) : '');
    setForm({
      topic_id:      q.topic_id,
      question_text: q.question_text,
      option_a:      q.option_a,
      option_b:      q.option_b,
      option_c:      q.option_c,
      option_d:      q.option_d,
      correct_option: q.correct_option,
      difficulty:    q.difficulty || 'medium',
      question_type: q.question_type || 'mcq',
      explanation:   q.explanation   || '',
      marks_correct: q.marks_correct ?? 4,
      marks_wrong:   q.marks_wrong   ?? -1,
      year:          q.year          || '',
      source:        q.source        || '',
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const cancelForm = () => {
    setShowForm(false); setEditing(null); setError('');
    setFormSubject(''); setFormChapter('');
    setForm(emptyQ);
  };

  const handleDelete = (q) => {
    const preview = stripHtml(q.question_text).slice(0, 80);
    if (window.confirm(`Delete question #${q.id}?\n\n"${preview}…"\n\nThis cannot be undone.`)) {
      deleteQ.mutate(q.id);
    }
  };

  // ── render ─────────────────────────────────────────────────────────────
  return (
    <div>
      {/* View modal */}
      {viewQ && <ViewModal question={viewQ} onClose={() => setViewQ(null)} />}

      {/* Page header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div>
          <h1 style={{ fontSize: 26, marginBottom: 4 }}>Questions</h1>
          <p style={{ color: 'var(--text-muted)', fontSize: 14 }}>
            {questionsQuery.data
              ? `${sortedQuestions.length} of ${questionsQuery.data.length} questions`
              : 'Loading…'}
          </p>
        </div>
        <button className="btn btn-primary"
          onClick={() => { cancelForm(); setShowForm(true); }}>
          + Add Question
        </button>
      </div>

      {/* Filter bar */}
      <div className="card" style={{ marginBottom: 20, padding: '14px 16px' }}>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
          <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-muted)' }}>Filter:</span>
          <select className="form-select" style={{ width: 160 }} value={filter.subject_id}
            onChange={e => setFilter({ subject_id: e.target.value, chapter_id: '', topic_id: '' })}>
            <option value="">All Subjects</option>
            {subjects.data?.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
          {filter.subject_id && (
            <select className="form-select" style={{ width: 200 }} value={filter.chapter_id}
              onChange={e => setFilter(f => ({ ...f, chapter_id: e.target.value, topic_id: '' }))}>
              <option value="">All Chapters</option>
              {filterChapters.data?.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          )}
          {filter.chapter_id && (
            <select className="form-select" style={{ width: 200 }} value={filter.topic_id}
              onChange={e => setFilter(f => ({ ...f, topic_id: e.target.value }))}>
              <option value="">All Topics</option>
              {filterTopics.data?.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          )}
          {(filter.subject_id || filter.chapter_id || filter.topic_id) && (
            <button className="btn btn-ghost btn-sm"
              onClick={() => setFilter({ subject_id: '', chapter_id: '', topic_id: '' })}>
              Clear
            </button>
          )}
        </div>
      </div>

      {/* Add / Edit form */}
      {showForm && (
        <div className="card" style={{ marginBottom: 24 }}>
          <h3 style={{ marginBottom: 20 }}>{editing ? `Edit Question #${editing}` : 'Add New Question'}</h3>
          <form onSubmit={handleSubmit}>
            {/* Subject / Chapter / Topic selectors */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginBottom: 16 }}>
              <div className="form-group">
                <label className="form-label">Subject *</label>
                <select className="form-select" value={formSubject}
                  onChange={e => { setFormSubject(e.target.value); setFormChapter(''); setForm(f => ({ ...f, topic_id: '' })); }}>
                  <option value="">Select Subject</option>
                  {subjects.data?.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
                <InlineCreate label="Subject" onSave={(name) => createSubjectMut.mutate({ name })} />
              </div>
              <div className="form-group">
                <label className="form-label">Chapter *</label>
                <select className="form-select" value={formChapter} disabled={!formSubject}
                  onChange={e => { setFormChapter(e.target.value); setForm(f => ({ ...f, topic_id: '' })); }}>
                  <option value="">Select Chapter</option>
                  {formChapters.data?.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
                {formSubject && (
                  <InlineCreate label="Chapter"
                    onSave={(name) => createChapterMut.mutate({ sid: parseInt(formSubject), name })} />
                )}
              </div>
              <div className="form-group">
                <label className="form-label">Topic *</label>
                <select className="form-select" value={form.topic_id} disabled={!formChapter}
                  onChange={e => setForm(f => ({ ...f, topic_id: e.target.value }))}>
                  <option value="">Select Topic</option>
                  {formTopics.data?.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
                {formChapter && (
                  <InlineCreate label="Topic"
                    onSave={(name) => createTopicMut.mutate({ cid: parseInt(formChapter), name })} />
                )}
              </div>
            </div>

            {/* Question type */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginBottom: 16 }}>
              <div className="form-group">
                <label className="form-label">Question Type</label>
                <select className="form-select" value={form.question_type}
                  onChange={e => setForm(f => ({ ...f, question_type: e.target.value }))}>
                  <option value="mcq">MCQ (Single Best)</option>
                  <option value="assertion_reason">Assertion & Reason</option>
                  <option value="match_the_following">Match the Following</option>
                  <option value="image_mcq">Image-based MCQ</option>
                  <option value="passage_based">Passage Based</option>
                  <option value="how_many_correct">How Many Correct</option>
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Difficulty</label>
                <select className="form-select" value={form.difficulty}
                  onChange={e => setForm(f => ({ ...f, difficulty: e.target.value }))}>
                  <option value="easy">Easy</option>
                  <option value="medium">Medium</option>
                  <option value="hard">Hard</option>
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Correct Answer *</label>
                <select className="form-select" value={form.correct_option}
                  onChange={e => setForm(f => ({ ...f, correct_option: e.target.value }))}>
                  <option value="A">A</option>
                  <option value="B">B</option>
                  <option value="C">C</option>
                  <option value="D">D</option>
                </select>
              </div>
            </div>

            {/* Question text */}
            <div className="form-group" style={{ marginBottom: 16 }}>
              <label className="form-label">Question Text * (HTML allowed)</label>
              <textarea className="form-textarea" rows={4} value={form.question_text} required
                onChange={e => setForm(f => ({ ...f, question_text: e.target.value }))} />
            </div>

            {/* Options */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
              {['a', 'b', 'c', 'd'].map(opt => (
                <div key={opt} className="form-group">
                  <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={{
                      width: 22, height: 22, borderRadius: 6, flexShrink: 0,
                      background: form.correct_option === opt.toUpperCase() ? 'var(--success)' : 'var(--border)',
                      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                      color: form.correct_option === opt.toUpperCase() ? 'white' : 'var(--text-muted)',
                      fontSize: 11, fontWeight: 700,
                    }}>{opt.toUpperCase()}</span>
                    Option {opt.toUpperCase()} *
                  </label>
                  <input className="form-input" value={form[`option_${opt}`]} required
                    onChange={e => setForm(f => ({ ...f, [`option_${opt}`]: e.target.value }))} />
                </div>
              ))}
            </div>

            {/* Explanation, marks, year, source */}
            <div className="form-group" style={{ marginBottom: 16 }}>
              <label className="form-label">Explanation (optional, HTML allowed)</label>
              <textarea className="form-textarea" rows={3} value={form.explanation}
                onChange={e => setForm(f => ({ ...f, explanation: e.target.value }))} />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 12, marginBottom: 16 }}>
              <div className="form-group">
                <label className="form-label">Marks (correct)</label>
                <input className="form-input" type="number" step="0.5" value={form.marks_correct}
                  onChange={e => setForm(f => ({ ...f, marks_correct: e.target.value }))} />
              </div>
              <div className="form-group">
                <label className="form-label">Marks (wrong)</label>
                <input className="form-input" type="number" step="0.5" value={form.marks_wrong}
                  onChange={e => setForm(f => ({ ...f, marks_wrong: e.target.value }))} />
              </div>
              <div className="form-group">
                <label className="form-label">Year</label>
                <input className="form-input" type="number" placeholder="e.g. 2024" value={form.year}
                  onChange={e => setForm(f => ({ ...f, year: e.target.value }))} />
              </div>
              <div className="form-group">
                <label className="form-label">Source</label>
                <input className="form-input" placeholder="e.g. Allen, NEET PG" value={form.source}
                  onChange={e => setForm(f => ({ ...f, source: e.target.value }))} />
              </div>
            </div>

            {error && <div className="error-msg" style={{ marginBottom: 12 }}>{error}</div>}
            <div style={{ display: 'flex', gap: 10 }}>
              <button className="btn btn-primary" type="submit"
                disabled={createQ.isPending || updateQ.isPending}>
                {editing ? 'Update Question' : 'Add Question'}
              </button>
              <button className="btn btn-ghost" type="button" onClick={cancelForm}>Cancel</button>
            </div>
          </form>
        </div>
      )}

      {/* Questions table */}
      <div className="card" style={{ padding: 0, overflowX: 'auto' }}>
        {questionsQuery.isLoading ? (
          <div className="spinner" style={{ padding: 40 }} />
        ) : questionsQuery.isError ? (
          <div style={{ padding: 24, color: 'var(--error, #ef4444)' }}>
            Failed to load questions. Try refreshing.
          </div>
        ) : (
          <table className="table" style={{ minWidth: 900 }}>
            <thead>
              <tr>
                <th style={{ width: 50, cursor: 'pointer' }} onClick={() => toggleSort('id')}>
                  # <SortIcon col="id" />
                </th>
                <th style={{ width: 120, cursor: 'pointer' }} onClick={() => toggleSort('subject_name')}>
                  Subject <SortIcon col="subject_name" />
                </th>
                <th style={{ width: 160, cursor: 'pointer' }} onClick={() => toggleSort('chapter_name')}>
                  Chapter <SortIcon col="chapter_name" />
                </th>
                <th>Question</th>
                <th style={{ width: 70 }}>Type</th>
                <th style={{ width: 70 }}>Ans</th>
                <th style={{ width: 80 }}>Difficulty</th>
                <th style={{ width: 130 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {sortedQuestions.length === 0 ? (
                <tr>
                  <td colSpan={8} className="empty-state">No questions found.</td>
                </tr>
              ) : sortedQuestions.map(q => (
                <tr key={q.id}>
                  <td style={{ color: 'var(--text-muted)', fontSize: 13 }}>{q.id}</td>
                  <td style={{ fontSize: 13 }}>
                    {q.subject_name
                      ? <span className="badge badge-blue" style={{ fontSize: 11 }}>{q.subject_name}</span>
                      : <span style={{ color: 'var(--text-muted)' }}>—</span>}
                  </td>
                  <td style={{ fontSize: 13, color: 'var(--text-secondary, var(--text-muted))' }}>
                    {q.chapter_name || '—'}
                  </td>
                  <td style={{ maxWidth: 340 }}>
                    <span style={{ fontSize: 14 }}>
                      {stripHtml(q.question_text).slice(0, 90)}
                      {stripHtml(q.question_text).length > 90 ? '…' : ''}
                    </span>
                    {q.topic_name && (
                      <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>
                        {q.topic_name}
                      </div>
                    )}
                  </td>
                  <td>
                    <span style={{
                      fontSize: 11, padding: '2px 6px', borderRadius: 4,
                      background: 'var(--surface-alt, #f5f5f5)', color: 'var(--text-muted)',
                    }}>
                      {QTYPE_LABEL[q.question_type] || q.question_type || 'MCQ'}
                    </span>
                  </td>
                  <td>
                    <span style={{ fontWeight: 700, color: 'var(--success)', fontSize: 15 }}>
                      {q.correct_option}
                    </span>
                  </td>
                  <td>
                    {q.difficulty
                      ? <span className={`badge ${DIFF_BADGE[q.difficulty] || 'badge-blue'}`}>{q.difficulty}</span>
                      : <span style={{ color: 'var(--text-muted)', fontSize: 13 }}>—</span>}
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button className="btn btn-ghost btn-sm" title="View"
                        onClick={() => setViewQ(q)}
                        style={{ padding: '4px 8px' }}>👁</button>
                      <button className="btn btn-ghost btn-sm" title="Edit"
                        onClick={() => startEdit(q)}
                        style={{ padding: '4px 8px' }}>✏️</button>
                      <button className="btn btn-danger btn-sm" title="Delete"
                        onClick={() => handleDelete(q)}
                        style={{ padding: '4px 8px' }}>🗑</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
